// Shared vocabulary and helpers for the Resources hub (public pages and the admin editor).
// Keep the keys in step with backend/services/resources_hub.py.
import {
  BookOpen, ListChecks, FileText, Wrench, PlayCircle, FileSpreadsheet, Settings2, ShieldCheck,
  HelpCircle, Presentation, Megaphone, ExternalLink,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

export const RESOURCE_TYPES = {
  user_guide: { label: 'User guide', plural: 'User guides', icon: BookOpen },
  quick_reference: { label: 'Quick reference', plural: 'Quick references', icon: ListChecks },
  how_to: { label: 'How-to', plural: 'How-to', icon: Wrench },
  article: { label: 'Article', plural: 'Articles', icon: FileText },
  video: { label: 'Video', plural: 'Videos', icon: PlayCircle },
  datasheet: { label: 'Datasheet', plural: 'Datasheets', icon: FileSpreadsheet },
  device_setup: { label: 'Device setup', plural: 'Device setup', icon: Settings2 },
  compliance: { label: 'Compliance', plural: 'Compliance', icon: ShieldCheck },
  faq: { label: 'FAQ', plural: 'FAQ', icon: HelpCircle },
  webinar: { label: 'Webinar', plural: 'Webinars', icon: Presentation },
  release_notes: { label: 'Release notes', plural: 'Release notes', icon: Megaphone },
  link: { label: 'Link', plural: 'Links', icon: ExternalLink },
};

export const RESOURCE_PRODUCTS = {
  phone_system: 'Phone System',
  sip_trunk: 'SIP Trunk',
  numbers: 'Phone numbers',
  webphone: 'WebPhone',
  mobile_app: 'Mobile app',
  teams: 'Microsoft Teams',
  ai_receptionist: 'AI Receptionist',
  hardware: 'Hardware',
  portal: 'Web portal',
};

export const typeInfo = (t) => RESOURCE_TYPES[t] || RESOURCE_TYPES.article;

// Files uploaded in the admin panel are served by the API; static files (e.g. /downloads/...) by the website.
export const assetUrl = (u) => {
  if (!u) return '';
  return u.startsWith('/api/') ? `${BACKEND_URL}${u}` : u;
};

export const formatSize = (bytes) => {
  if (!bytes) return '';
  if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

export const formatDate = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
};

const fileKind = (f) => (f?.ext || '').toUpperCase() || 'File';

// "PDF · 10 pages", "5 min read", "Video · 3:20"
export const metaParts = (r) => {
  const parts = [];
  if (r.type === 'video' || r.type === 'webinar') {
    parts.push(r.duration ? `Video · ${r.duration}` : 'Video');
  } else if (r.file) {
    const kind = fileKind(r.file);
    if (r.file.pages) parts.push(`${kind} · ${r.file.pages} page${r.file.pages === 1 ? '' : 's'}`);
    else if (r.file.size) parts.push(`${kind} · ${formatSize(r.file.size)}`);
    else parts.push(kind);
  } else if (r.read_minutes) {
    parts.push(`${r.read_minutes} min read`);
  } else if (r.type === 'link' && r.external_url) {
    try { parts.push(new URL(r.external_url).hostname.replace(/^www\./, '')); } catch (e) { /* ignore */ }
  }
  return parts;
};

const READ_FIRST = ['article', 'how_to', 'faq'];

// The main thing a card does: download/view a file, read, watch or open a link.
export const primaryAction = (r) => {
  if (r.type === 'video' || r.type === 'webinar') return { label: 'Watch', kind: 'page' };
  if (r.type === 'link' && r.external_url) return { label: 'Open link', kind: 'external', href: r.external_url };
  if (r.file && (!r.has_body || !READ_FIRST.includes(r.type))) {
    const ext = (r.file.ext || '').toLowerCase();
    return { label: ext === 'pdf' ? 'Download PDF' : `Download ${fileKind(r.file)}`, kind: 'file', href: assetUrl(r.file.url) };
  }
  return { label: 'Read', kind: 'page' };
};

// Privacy-friendly embeds: youtube-nocookie and Vimeo with do-not-track; anything else must be a direct video file.
export const videoEmbed = (url) => {
  if (!url) return null;
  let u;
  try { u = new URL(url); } catch (e) { return null; }
  if (u.protocol !== 'https:' && u.protocol !== 'http:') return null;
  const host = u.hostname.replace(/^www\.|^m\./, '');
  let id = null;
  if (host === 'youtu.be') id = u.pathname.slice(1);
  else if (host === 'youtube.com' || host === 'youtube-nocookie.com') {
    id = u.searchParams.get('v') || (u.pathname.match(/^\/(?:embed|shorts|live)\/([^/?]+)/) || [])[1];
  }
  if (id && /^[A-Za-z0-9_-]{6,20}$/.test(id)) {
    return { kind: 'iframe', src: `https://www.youtube-nocookie.com/embed/${id}?rel=0&modestbranding=1` };
  }
  if (host === 'vimeo.com' || host === 'player.vimeo.com') {
    const v = (u.pathname.match(/(\d{6,12})/) || [])[1];
    if (v) return { kind: 'iframe', src: `https://player.vimeo.com/video/${v}?dnt=1` };
  }
  if (/\.(mp4|webm)$/i.test(u.pathname) || u.pathname.startsWith('/api/resources/files/')) {
    return { kind: 'video', src: url };
  }
  return null;
};
